import { useIsMobile } from '@duke-hq/ui/components/Breakpoints/hooks';
import { useCallback, useMemo, useState } from 'react';
import type { ChatMeta, ChatUser } from '@duke-hq/svc-support-web';
import type { Drn } from '@duke-hq/drn/type';
import { ListDetail, ListProps } from '@duke-hq/ui/components/ListDetail/ListDetail';
import { Flex, Text, IconButton } from '@chakra-ui/react';
import { t } from '@lingui/core/macro';
import { useNavigate } from 'react-router-dom';
import { SupportContext } from './support/ctx';
import { ChatListItems } from './support/ChatList';
import { Detail } from './support/ChatDetail';
import { useChatActions } from './support/useChatActions';
import { PlusIcon } from 'lucide-react';
import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';

export function SupportStudent({
  studentDrn,
  selectedChat,
}: {
  studentDrn: Drn;
  selectedChat?: string;
}) {
  const isCondensed = useIsMobile();
  const [currentPage, setCurrentPage] = useState(0);
  const navigate = useNavigate();

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    hasPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
  } = trpcAdmin.support.chat.listStudent.useInfiniteQuery(
    {
      studentDrn,
    },
    {
      getNextPageParam: (lastPage) => lastPage?.cursor,
    },
  );

  const lookupUser = useCallback(
    (userDrn: Drn) => {
      if (data) {
        for (const page of data.pages) {
          if (!page) {
            continue;
          }

          if (page.users[userDrn]) {
            return page.users[userDrn] as ChatUser | undefined;
          }
        }
      }
    },
    [data],
  );

  const { chatActions, setChatUserCache } = useChatActions({ lookupUser });

  useMemo(() => {
    setChatUserCache((prev) => {
      const cache = { ...prev };
      if (!data) {
        return cache;
      }

      for (const page of data.pages) {
        if (!page) {
          continue;
        }

        for (const userDrn in page.users) {
          if (!cache[userDrn]) {
            cache[userDrn] = page.users[userDrn];
          }
        }
      }

      return cache;
    });
  }, [data, setChatUserCache]);

  const nextPage = useCallback(async () => {
    // The next page has not yet been populated
    if (hasNextPage) {
      await fetchNextPage();
    }
    if (!data) {
      return;
    }

    if (data?.pages.length > currentPage) {
      setCurrentPage(currentPage + 1);
    }
  }, [currentPage, hasNextPage, data, fetchNextPage]);

  const previousPage = useCallback(() => {
    if (currentPage === 0) {
      return;
    }
    setCurrentPage(currentPage - 1);
  }, [currentPage]);

  const { items, showNextPage, showPreviousPage } = useMemo(() => {
    let showNextPage,
      showPreviousPage = false;

    let items = new Map<string, ChatMeta>();

    if (!data || !data.pages) {
      return { items, showNextPage, showPreviousPage };
    }

    if (data.pages.length === 0) {
      return { items, showNextPage, showPreviousPage };
    }

    if (hasNextPage || currentPage < data.pages.length - 1) {
      showNextPage = true;
    }
    if (hasPreviousPage || currentPage > 0) {
      showPreviousPage = true;
    }

    const pageIndex = Math.min(currentPage, data.pages.length - 1);

    const page = data.pages[pageIndex];
    if (page?.chats) {
      items = new Map(page.chats.map((item) => [item.id, item]));
    }

    return {
      items,
      showNextPage,
      showPreviousPage,
    };
  }, [hasNextPage, currentPage, data, hasPreviousPage]);

  const onSelect = useCallback(
    (id: string) => {
      if (selectedChat) {
        navigate(`../${id}`, { relative: 'path' });
      } else {
        navigate(`./${id}`, { relative: 'path' });
      }
    },
    [navigate, selectedChat],
  );

  const createChat = trpcAdmin.support.createGeneralSupportChatWithStudent.useMutation({
    onSuccess: (data) => {
      if (data.id) {
        onSelect(data.id);
      }
    },
    onSettled: () => {
      void utils.support.chat.listStudent.invalidate();
    },
  });
  const utils = trpcAdmin.useUtils();

  const onCreateChat = useCallback(() => {
    createChat.mutate({
      studentDrn,
    });
  }, [createChat, studentDrn]);

  // const List = useMemo(ChatListWithStudentDrn({ onCreateChat }), [onCreateChat]);
  // Use a HOC as we only need to pass along a single prop
  // otherwise we could use Context.

  const List = useMemo(
    () =>
      function ChatList(props: ListProps<ChatMeta, string>) {
        return (
          <Flex direction="column" alignSelf="stretch" w="100%" h="100%" overflow="auto">
            <Flex
              alignItems="center"
              alignSelf="stretch"
              justifyContent="space-between"
              p="16px 16px 8px 16px"
            >
              <Text textStyle="2xl" fontWeight="semibold" textTransform="capitalize">
                {t`Chats`}
              </Text>
              <IconButton
                rounded="full"
                size="sm"
                variant="outline"
                colorScheme="gray"
                aria-label={t`create chat`}
                icon={<PlusIcon size={20} />}
                onClick={onCreateChat}
              />
            </Flex>
            <ChatListItems {...props} />
          </Flex>
        );
      },
    [onCreateChat],
  );

  return (
    <SupportContext.Provider value={chatActions}>
      <ListDetail
        List={List}
        Detail={Detail}
        items={items}
        onSelect={onSelect}
        selectedId={selectedChat}
        isCondensed={isCondensed}
        isFetchingNextPage={isFetchingNextPage}
        isFetchingPreviousPage={isFetchingPreviousPage}
        hasNextPage={showNextPage}
        hasPreviousPage={showPreviousPage}
        isLoading={isLoading}
        isError={isError}
        nextPage={nextPage}
        previousPage={previousPage}
      />
    </SupportContext.Provider>
  );
}
